import * as React from "react";

import { UitkCard, UitkCardLink } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkFigure } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkRating } from "uitk-react-rating";
import { UitkPriceLockupAlign } from "uitk-react-price-lockup";
import { useLocalization } from "@shared-ui/localization-context";

import PriceLockup from "components/flexComponents/Hotels/views/HotelsViews/components/shared/PriceLockup";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";
import DescriptionOrReview from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DescriptionOrReview";
import AmenitiesOrRecommendation from "components/flexComponents/Hotels/views/HotelsViews/components/shared/AmenitiesOrRecommendation";
import ValueProposition from "components/flexComponents/Hotels/views/HotelsViews/components/shared/ValueProposition";
import { getHotelLink, getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";

import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { ratingIcon } from "components/utility/RatingUtil";
import { TrackedLink } from "components/utility/analytics/TrackedLink";

import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { HotelPriceLockup } from "src/components/flexComponents/Hotels/utils/A11y/HotelPriceLockup";
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";

interface HotelImageCardProps {
  context: ExtendedContextStore;
  model: HotelsFlexModuleResult;
  hotel: Hotel;
}

const HotelCard = (props: HotelImageCardProps) => {
  const { context, model, hotel } = props;
  const { callToAction, destinationId } = model;
  const { hotelName, hotelStarRating, hotelFormattedFullAddress } = hotel;

  const { formatText } = useLocalization();

  const hotelImageUrl = getOptimizedImageUrl(hotel, context);

  return (
    <UitkCard>
      <UitkLayoutFlex>
        <UitkLayoutFlexItem minWidth="half_width" shrink={0}>
          <div>
            <UitkFigure className="all-image-fit-cover">
              <LazyLoadedImage src={hotelImageUrl} alt={hotelName} context={context} />
            </UitkFigure>
          </div>
        </UitkLayoutFlexItem>

        <UitkLayoutFlexItem grow={1}>
          <div>
            <UitkLayoutFlex direction="column" className="all-x-padding-three all-y-padding-three">
              <UitkLayoutFlex justifyContent="space-between">
                <UitkLayoutFlex direction="column">
                  <UitkHeading tag="h3" size={5} overflow="truncate-2-lines">
                    {hotelName}
                  </UitkHeading>
                  <UitkRating
                    rating={hotelStarRating}
                    icon={ratingIcon(context.locale)}
                    className={context.site.brand}
                  />
                  <UitkText size={300}>{hotelFormattedFullAddress}</UitkText>
                </UitkLayoutFlex>
                <DistanceOrRating model={model} hotel={hotel} className="uitk-type-right" context={context} />
              </UitkLayoutFlex>
              <PriceLockup
                hotel={hotel}
                align={UitkPriceLockupAlign.LEFT}
                className="all-t-margin-three"
                priceA11y={new HotelPriceLockup(hotel, formatText)}
                hasGetRatesText
              />
              <DescriptionOrReview model={model} hotel={hotel} className="all-t-margin-three" />
              <AmenitiesOrRecommendation model={model} hotel={hotel} className="all-t-margin-three" />
              <ValueProposition hotel={hotel} className="all-t-margin-three" />
            </UitkLayoutFlex>
          </div>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>

      <UitkCardLink>
        <TrackedLink
          data-testid={`hotelcard-link-${hotelName}`}
          aria-label={hotelName}
          moduleName="hotels"
          rfrr="card"
          rel={getHotelLinkRel(callToAction)}
          href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
        />
      </UitkCardLink>
    </UitkCard>
  );
};

export default HotelCard;
